import { Component, Input, OnInit, AfterViewInit, OnDestroy, ElementRef, NgZone } from '@angular/core';
import { MenuController, ModalController, GestureController, Gesture } from '@ionic/angular';
import { Subject, Subscription } from 'rxjs';
import { ChatService, IChatState } from 'src/app/services';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.page.html',
  styleUrls: ['./chat.page.scss'],
})
export class ChatPage implements OnInit, AfterViewInit, OnDestroy {

  public activeConversationId = null;
  public activeConversationOptions: any;
  public mode: "messages" | "conversation" = "messages"; 
  private chatSubject: Subject<IChatState>;
  private chatSubscription: Subscription;
  private swipeGesture: Gesture;

  @Input() state: IChatState;

  constructor(
    private chatService: ChatService,
    private menuCtrl: MenuController,
    private modalCtrl: ModalController,
    private gestureCtrl: GestureController,
    private el: ElementRef,
    private zone: NgZone
  ) {  }

  ngOnInit() {
    if(this.state) {
      if(this.state.mode === "conversation") {
        this.setActiveConversaiton(this.state.id, this.state.options);
      } else {
        this.removeActiveConversation();
      }
    }
  }

  ngAfterViewInit() {
    this.setupSwipeGesture();
  }

  ngOnDestroy() {
    if(this.chatSubscription) {
      this.chatSubscription.unsubscribe();
    }
    if(this.swipeGesture) {
      this.swipeGesture.destroy();
    }
  }

  private setupSwipeGesture() {
    // Wait for the modal to be fully rendered
    setTimeout(() => {
      const element = this.el.nativeElement;
      const pageElement = element.querySelector('.ion-page') || element;
      // Query for app-conversation inside handlers so we get the current element
      // (with *ngIf it only exists when mode === 'conversation')
      let startX = 0;
      let currentX = 0;
      let isDragging = false;

      this.swipeGesture = this.gestureCtrl.create({
        el: pageElement,
        gestureName: 'swipe-to-close',
        threshold: 15,
        direction: 'x',
        onStart: (ev) => {
          startX = ev.currentX;
          currentX = startX;
          isDragging = true;
        },
        onMove: (ev) => {
          if (!isDragging) return;
          currentX = ev.currentX;
          const deltaX = currentX - startX;

          if (deltaX > 0) {
            const width = pageElement.offsetWidth || window.innerWidth;
            const translateX = Math.min(deltaX, width);

            if (this.mode === "conversation") {
              const conversationEl = element.querySelector('app-conversation') as HTMLElement;
              if (conversationEl) {
                conversationEl.style.transform = `translateX(${translateX}px)`;
                conversationEl.style.transition = 'none';
                conversationEl.style.opacity = String(Math.max(0.7, 1 - (deltaX / width) * 0.3));
              }
            } else if (this.mode === "messages") {
              pageElement.style.transform = `translateX(${translateX}px)`;
              pageElement.style.transition = 'none';
              pageElement.style.opacity = String(Math.max(0.5, 1 - (deltaX / width) * 0.5));
            }
          }
        },
        onEnd: () => {
          if (!isDragging) return;
          isDragging = false;

          const deltaX = currentX - startX;
          const width = pageElement.offsetWidth || window.innerWidth;
          const threshold = width * 0.3;

          if (deltaX > threshold && deltaX > 0) {
            this.zone.run(() => {
              if (this.mode === "conversation") {
                const conversationEl = element.querySelector('app-conversation') as HTMLElement;
                const messagesElement = element.querySelector('app-messages') as HTMLElement;
                if (messagesElement) {
                  messagesElement.style.display = 'block';
                  messagesElement.style.visibility = 'visible';
                }
                if (conversationEl) {
                  conversationEl.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
                  conversationEl.style.transform = `translateX(${width}px)`;
                  conversationEl.style.opacity = '0';
                }
                setTimeout(() => {
                  this.removeActiveConversation();
                  if (conversationEl) {
                    conversationEl.style.transform = 'translateX(0)';
                    conversationEl.style.opacity = '1';
                  }
                  if (messagesElement) {
                    messagesElement.style.display = '';
                    messagesElement.style.visibility = '';
                  }
                }, 300);
              } else if (this.mode === "messages") {
                pageElement.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
                pageElement.style.transform = `translateX(${width}px)`;
                pageElement.style.opacity = '0';
                setTimeout(() => this.modalCtrl.dismiss(), 300);
              }
            });
          } else {
            if (this.mode === "conversation") {
              const conversationEl = element.querySelector('app-conversation') as HTMLElement;
              if (conversationEl) {
                conversationEl.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
                conversationEl.style.transform = 'translateX(0)';
                conversationEl.style.opacity = '1';
              }
            } else {
              pageElement.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
              pageElement.style.transform = 'translateX(0)';
              pageElement.style.opacity = '1';
            }
          }
        }
      });

      this.swipeGesture.enable(true);
    }, 100);
  }

  removeActiveConversation() {
    this.mode = "messages";
    this.activeConversationId = null;
    this.activeConversationOptions = null;
  }

  setActiveConversaiton(conversaitonId: string, options?: any) {
    this.activeConversationId = conversaitonId;
    this.activeConversationOptions = options;
    this.mode = "conversation";
  }

  conversationSelected(convoId: string) {
    this.setActiveConversaiton(convoId);
  }

  conversationClosed() {
    this.removeActiveConversation();
  }

  closeMessages() {
    this.modalCtrl.dismiss();
  }
}
